import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams } from "react-router";
import { useApi } from "@/hooks/useApi";
import { useEncryption } from "@/hooks/useEncryption";
import { Formik, Form, Field } from "formik";
import { type } from "arktype";
import { arktypeToFormikValidate } from "@/lib/validation";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MarkdownText } from "@/components/shared/MarkdownText";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { LoadingSpinner } from "@/components/shared/LoadingSpinner";
import { EmptyState } from "@/components/shared/EmptyState";
import { IconPicker } from "@/components/shared/IconPicker";
import { FrequencyPicker } from "@/components/shared/FrequencyPicker";
import { WeekdayToggle } from "@/components/shared/WeekdayToggle";
import { ChoreTimer } from "@/components/chores/ChoreTimer";
import { showToast } from "@/components/shared/Toast";
import { BottomDrawer } from "@/components/shared/BottomDrawer";
import { Plus, X, Zap } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useConfirm } from "@/components/shared/ConfirmDialog";

const choreSchema = type({
  name: "string >= 1",
  icon: "string",
  description: "string",
  frequency_in_days: "number.integer >= 1",
  weekdays: "string",
  atHour: "0 <= number.integer <= 23",
  gracetime_in_hours: "number.integer >= 0",
  reward_points: "number.integer >= 0",
  punishment_points: "number.integer >= 0",
});
const validateChore = arktypeToFormikValidate(choreSchema);

interface PendingInfo {
  status: "pending" | "due" | "completed" | "failed";
  due_at: string;
  expires_at: string;
  completed_at?: string | null;
}

interface Chore {
  _key: string;
  name: string;
  notification_name: string;
  icon: string;
  description: string;
  is_active: boolean;
  frequency_in_days: number;
  weekdays: string;
  atHour: number;
  gracetime_in_hours: number;
  reward_points: number;
  punishment_points: number;
  max_points: number | null;
  pending: PendingInfo | null;
}

export function AdminChores() {
  const { jwt, clientId } = useParams();
  const { getHeaders } = useAuth();
  const { encryptFields, decryptList, hasKey } = useEncryption(clientId);
  const { data: rawData, loading, error, refetch } = useApi<{ chores: Chore[]; current_points: number }>(
    `/api/admin/${jwt}/${clientId}/chores`,
  );
  const currentPoints = rawData?.current_points ?? 0;
  const [chores, setChores] = useState<Chore[] | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingChore, setEditingChore] = useState<Chore | null>(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const { confirm, dialog: confirmDialog } = useConfirm();

  // Decrypt chore names/descriptions after fetch
  useEffect(() => {
    if (!rawData?.chores) return;
    decryptList(rawData.chores as unknown as Record<string, unknown>[], ["name", "description"])
      .then((d) => setChores(d as unknown as Chore[]));
  }, [rawData?.chores, decryptList]);

  const handleSubmit = useCallback(async (rawValues: Record<string, unknown>) => {
    const base = { ...rawValues, max_points: rawValues.max_points === "" || rawValues.max_points === undefined ? null : Number(rawValues.max_points) };
    // Encrypt name and description before sending
    const values = await encryptFields(base as Record<string, unknown>, ["name", "description"]);
    setSubmitLoading(true);
    try {
      const url = editingChore
        ? `/api/admin/${jwt}/${clientId}/chores/${editingChore._key}`
        : `/api/admin/${jwt}/${clientId}/chores`;
      const method = editingChore ? "PUT" : "POST";
      const res = await fetch(url, { method, headers: getHeaders(), body: JSON.stringify(values) });
      if (!res.ok) { const data = await res.json(); throw new Error(data.error); }
      setShowForm(false);
      setEditingChore(null);
      refetch();
      showToast.success("Chore saved!");
    } catch (err) {
      showToast.error(err instanceof Error ? err.message : "Failed");
    } finally {
      setSubmitLoading(false);
    }
  }, [editingChore, jwt, clientId, encryptFields, getHeaders, refetch]);

  const handleDelete = useCallback(async (choreKey: string) => {
    const ok = await confirm({
      title: "Deactivate this chore?",
      description: "The chore will be hidden but its history preserved.",
      confirmLabel: "Deactivate",
      destructive: true,
    });
    if (!ok) return;
    await fetch(`/api/admin/${jwt}/${clientId}/chores/${choreKey}`, { method: "DELETE", headers: getHeaders() });
    refetch();
  }, [jwt, clientId, getHeaders, refetch, confirm]);

  const handlePermanentDelete = useCallback(async (choreKey: string) => {
    const ok = await confirm({
      title: "Permanently delete this chore?",
      description: "This will remove the chore and its edge completely. This cannot be undone.",
      confirmLabel: "Delete Forever",
      destructive: true,
    });
    if (!ok) return;
    await fetch(`/api/admin/${jwt}/${clientId}/chores/${choreKey}/permanent`, { method: "DELETE", headers: getHeaders() });
    refetch();
  }, [jwt, clientId, getHeaders, refetch, confirm]);

  if (loading) return <LoadingSpinner />;
  if (error) return <p className="text-app-danger text-body p-4">{error}</p>;

  return (
    <div className="space-y-3 md:space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-heading font-bold">Chores</h1>
        <Button onClick={() => { setShowForm(true); setEditingChore(null); }} className="bg-app-primary hover:bg-app-primary-hover">
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </div>

      <BottomDrawer open={showForm} onClose={() => { setShowForm(false); setEditingChore(null); }} title={editingChore ? "✏️ Edit Chore" : "✨ New Chore"}>
        {showForm && (
          <Formik
            initialValues={{
              name: editingChore?.name || "",
              notification_name: editingChore?.notification_name || "",
              icon: editingChore?.icon || "🧹",
              description: editingChore?.description || "",
              frequency_in_days: editingChore?.frequency_in_days || 1,
              weekdays: editingChore?.weekdays || "",
              atHour: editingChore?.atHour ?? 9,
              gracetime_in_hours: editingChore?.gracetime_in_hours ?? 2,
              reward_points: editingChore?.reward_points || 0,
              punishment_points: editingChore?.punishment_points || 0,
              max_points: editingChore?.max_points ?? "",
            }}
            validate={validateChore}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, setFieldValue }) => (
              <Form className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="space-y-1">
                    <Label>Icon</Label>
                    <IconPicker value={values.icon} onSelect={(emoji) => setFieldValue("icon", emoji)} />
                  </div>
                  <div className="flex-1 space-y-1">
                    <Label>Name</Label>
                    <Field as={Input} name="name" placeholder="Chore name" autoFocus />
                    {touched.name && errors.name && <p className="text-info text-app-danger">{errors.name}</p>}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label>Notification Name (optional, unencrypted)</Label>
                  <Field as={Input} name="notification_name" placeholder="e.g., 'Daily Tasks' - shown in push notifications" />
                  <p className="text-info text-app-text-muted">This name is sent unencrypted in notifications. Leave blank for generic "A chore is due" messages.</p>
                </div>
                <div className="space-y-1">
                  <Label>Description</Label>
                  <Field as={Textarea} name="description" placeholder="Optional..." />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Frequency</Label>
                    <FrequencyPicker value={values.frequency_in_days} onChange={(v) => setFieldValue("frequency_in_days", v)} />
                  </div>
                  <div className="space-y-1">
                    <Label>Weekdays</Label>
                    <WeekdayToggle value={values.weekdays} onChange={(v) => setFieldValue("weekdays", v)} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label>At Hour (0-23)</Label>
                    <Field as={Input} name="atHour" type="number" min={0} max={23} />
                  </div>
                  <div className="space-y-1">
                    <Label>Grace (hours)</Label>
                    <Field as={Input} name="gracetime_in_hours" type="number" min={0} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label>Reward Points</Label>
                    <Field as={Input} name="reward_points" type="number" min={0} />
                  </div>
                  <div className="space-y-1">
                    <Label>Punishment Points</Label>
                    <Field as={Input} name="punishment_points" type="number" min={0} />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label>Max Points (optional)</Label>
                  <Field as={Input} name="max_points" type="number" placeholder="No limit" />
                  <p className="text-info text-app-text-muted">Chore only active when partner's points are below this value. Leave empty for always active.</p>
                </div>
                <div className="flex gap-2 justify-end">
                  {editingChore && editingChore.is_active && (
                    <Button type="button" variant="outline" className="border-app-danger text-app-danger hover:bg-app-danger/10 mr-auto" onClick={() => { setShowForm(false); setEditingChore(null); handleDelete(editingChore._key); }}>
                      Deactivate
                    </Button>
                  )}
                  {editingChore && !editingChore.is_active && (
                    <Button type="button" variant="outline" className="border-app-danger text-app-danger hover:bg-app-danger/10 mr-auto" onClick={() => { setShowForm(false); setEditingChore(null); handlePermanentDelete(editingChore._key); }}>
                      Delete Permanently
                    </Button>
                  )}
                  <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditingChore(null); }}>Cancel</Button>
                  <Button type="submit" className="bg-app-primary hover:bg-app-primary-hover" disabled={submitLoading}>
                    {submitLoading ? "Saving..." : editingChore ? "Update" : "Create"}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </BottomDrawer>

      {!chores || chores.length === 0 ? (
        <EmptyState icon="🧹" title="No chores yet" description="Create your first chore!" />
      ) : (
        <div className="space-y-2">
          {[...chores]
            .sort((a, b) => {
              // Disabled at the very bottom
              if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
              // Among active: paused (due to filter) below active
              const aPaused = a.is_active && a.max_points != null && currentPoints >= a.max_points;
              const bPaused = b.is_active && b.max_points != null && currentPoints >= b.max_points;
              if (aPaused !== bPaused) return aPaused ? 1 : -1;
              // Paused: sort by max_points descending
              if (aPaused && bPaused) return (b.max_points ?? 0) - (a.max_points ?? 0);
              // Active: sort by server-driven status
              const statusA = a.pending?.status ?? "pending";
              const statusB = b.pending?.status ?? "pending";
              const priority: Record<string, number> = { due: 0, pending: 1, failed: 2, completed: 3 };
              const pA = priority[statusA] ?? 4;
              const pB = priority[statusB] ?? 4;
              if (pA !== pB) return pA - pB;
              if (statusA === "due" && a.pending && b.pending) return new Date(a.pending.expires_at).getTime() - new Date(b.pending.expires_at).getTime();
              if (statusA === "pending" && a.pending && b.pending) return new Date(a.pending.due_at).getTime() - new Date(b.pending.due_at).getTime();
              return 0;
            })
            .map((chore) => {
              const pointsPaused = chore.is_active && chore.max_points != null && currentPoints >= chore.max_points;
              const borderColor = !chore.is_active ? "border-l-app-text-muted" : pointsPaused ? "border-l-app-warning" : "border-l-app-success";
              const dimmed = !chore.is_active || pointsPaused;
              return (
            <Card key={chore._key} className={`border-l-4 ${borderColor} ${dimmed ? "opacity-60" : ""} hover:shadow-md hover:bg-app-surface-raised/50 transition-all duration-200 py-0 gap-0 cursor-pointer overflow-hidden`} onClick={() => { setEditingChore(chore); setShowForm(true); }}>
              <CardContent className="p-2 md:p-2.5">
                <div className="flex items-center gap-2.5">
                  <div className={`w-8 h-8 rounded-lg ${!chore.is_active ? "bg-app-surface-raised" : pointsPaused ? "bg-app-warning/10" : "bg-app-success/10"} flex items-center justify-center text-lg shrink-0`}>{chore.icon}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-body font-semibold">{chore.name}</h3>
                      {!chore.is_active ? (
                        <span className="text-xs bg-app-surface-raised text-app-text-muted px-2 py-0.5 rounded-full font-medium">Disabled</span>
                      ) : pointsPaused ? (
                        <span className="text-xs bg-app-warning/10 text-app-warning px-2 py-0.5 rounded-full font-bold inline-flex items-center gap-1">
                          <Zap className="w-3 h-3" /> Paused above {chore.max_points} pts
                        </span>
                      ) : chore.is_active ? (
                        <ChoreTimer
                          pending={chore.pending}
                          compact
                        />
                      ) : null}
                    </div>
                    {chore.description && (
                      <MarkdownText text={chore.description} className="text-info text-app-text-muted mt-0.5" />
                    )}
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      <span className="text-info bg-app-primary/10 text-app-primary px-2 py-0.5 rounded-full">Every {chore.frequency_in_days}d</span>
                      {chore.weekdays && <span className="text-info bg-app-info/10 text-app-info px-2 py-0.5 rounded-full">{chore.weekdays}</span>}
                      <span className="text-info bg-app-accent/10 text-app-accent px-2 py-0.5 rounded-full">At {chore.atHour}:00</span>
                      <span className="text-info bg-app-text-muted/10 text-app-text-muted px-2 py-0.5 rounded-full">Grace: {chore.gracetime_in_hours}h</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      <span className="text-info bg-app-success/10 text-app-success px-2 py-0.5 rounded-full font-medium">+{chore.reward_points}</span>
                      <span className="text-info bg-app-danger/10 text-app-danger px-2 py-0.5 rounded-full font-medium">-{chore.punishment_points}</span>
                      {chore.max_points != null && !pointsPaused && <span className="text-info bg-app-warning/10 text-app-warning px-2 py-0.5 rounded-full font-medium">⚡ &lt;{chore.max_points} pts</span>}
                    </div>
                  </div>
                  {chore.is_active && !pointsPaused ? (
                  <Button variant="ghost" size="icon" className="shrink-0" onClick={(e) => { e.stopPropagation(); handleDelete(chore._key); }}>
                    <X className="w-4 h-4 text-app-danger" />
                  </Button>
                  ) : pointsPaused ? (
                  <div className="shrink-0 rounded-lg w-8 h-8 bg-app-warning/10 flex items-center justify-center" title={`Paused — points at or above ${chore.max_points}`}>
                    <Zap className="w-4 h-4 text-app-warning" />
                  </div>
                  ) : null}
                </div>
              </CardContent>
            </Card>
              );
            })}
        </div>
      )}
      {confirmDialog}
    </div>
  );
}
